import React from "react";
import { useNavigate } from "react-router-dom";
import DashboardView from "../../components/dashboard/DashboardView";
import { computeDemoPosition, DEMO_COMMITMENTS, enrichDemo } from "../../lib/demoData";
import { Button } from "../../components/ui/button";
import { Info } from "lucide-react";
import Seo from "../../components/Seo";

export default function Demo() {
  const nav = useNavigate();
  const enriched = DEMO_COMMITMENTS.map(enrichDemo);
  const position = computeDemoPosition();
  const upcoming = enriched.filter((c) => c.status === "active" && c.days_remaining != null)
    .sort((a, b) => a.days_remaining - b.days_remaining).slice(0, 5);
  const attention = [];
  enriched.forEach((c) => {
    if (c.usage_flag === "unused") attention.push({ commitment: c, reason: "You marked this as no longer used" });
    if (c.days_remaining != null && c.days_remaining >= 0 && c.days_remaining <= 3) attention.push({ commitment: c, reason: "Due soon" });
    if (c.category === "emi" && c.total_instalments && (c.total_instalments - c.instalments_completed) <= 3) attention.push({ commitment: c, reason: "EMI progressing — review remaining tenure" });
  });
  const data = { position, upcoming, needs_attention: attention.slice(0, 5) };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      <Seo title="Demo" description="A fictional sample account showing the COMMITMENTT commitment position. All figures are fictional." />
      <div className="rounded-2xl bg-mint-light/60 border border-mint/20 px-5 py-4 mb-6 flex items-start gap-3" data-testid="demo-banner">
        <Info size={18} className="text-mint mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-ink">Demo Mode — fictional data</p>
          <p className="text-sm text-slatey">This is a sample account for “Arjun”. All figures are fictional and are not your own account.</p>
        </div>
      </div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-heading text-2xl font-medium text-ink">Arjun's commitment position</h1>
          <p className="text-sm text-muted-foreground">Salary date: 1st of every month · Fictional</p>
        </div>
        <Button className="rounded-full bg-navy text-white hover:bg-navy-light" onClick={() => nav("/register")} data-testid="demo-join-btn">Get Started Free</Button>
      </div>
      <DashboardView data={data} commitments={enriched} demo onSelect={() => {}} />
      <div className="mt-6 rounded-2xl bg-secondary/60 border border-border p-5" data-testid="demo-metric-note">
        <p className="text-sm font-medium text-ink mb-2">Why “Committed This Month” (₹29,846) differs from the ratio numerator (₹28,446)</p>
        <p className="text-sm text-slatey leading-relaxed">
          The two figures use different, intentional scopes and differ by <strong className="text-ink">₹1,400</strong>:
        </p>
        <ul className="text-sm text-slatey mt-2 space-y-1">
          <li>+ <strong className="text-ink">₹2,400</strong> BNPL one-time due — counted in <em>Committed This Month</em> (an actual deduction now) but excluded from the ratio (not a recurring monthly commitment).</li>
          <li>− <strong className="text-ink">₹1,000</strong> annual insurance monthly-equivalent (₹12,000 ÷ 12) — included in the ratio’s recurring total but not in <em>Committed This Month</em>, since it renews in a later month.</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-2">₹28,446 recurring + ₹2,400 one-time − ₹1,000 annualised = ₹29,846 due this month.</p>
      </div>
    </div>
  );
}
